// Google OAuth configuration
import { apiFetch } from "../../utils/api";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "558810603405-t0pa1ed1u21dp1rgogt6b9fbll719g01.apps.googleusercontent.com";
const REDIRECT_URI = `${window.location.origin}/auth/google/callback`;

// OAuth scopes we need
const SCOPES = [
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile"
];


/**
 * Initiates Google OAuth flow by redirecting to Google's authorization server
 * @param {string} mode - Either 'login' or 'register'
 */
export const initiateGoogleAuth = async (mode = 'login') => {
  // Store the mode in sessionStorage to use after callback
  sessionStorage.setItem('googleAuthMode', mode);
  
  // Build the Google OAuth URL
  const stateValue = generateStateParameter();

  // Save state on server for server-side validation (defense-in-depth)
  try {
    await apiFetch('/api/auth/google/state/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state: stateValue })
    });
  } catch (e) {
    // If this fails, still proceed; backend will reject if state mismatch
    // eslint-disable-next-line no-console
    console.warn('[GoogleAuth] Failed to save state on server', e);
  }

  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: SCOPES.join(' '),
    access_type: 'offline',
    prompt: 'consent',
    state: stateValue
  });

  const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  
  // Redirect to Google OAuth
  window.location.href = googleAuthUrl;
};

/**
 * Generates a random state parameter for OAuth security
 */
const generateStateParameter = () => {
  const state = Math.random().toString(36).substring(2, 15) + 
                Math.random().toString(36).substring(2, 15);
  sessionStorage.setItem('googleAuthState', state);
  return state;
};

/**
 * Handles the OAuth callback from Google
 * @param {string} code - Authorization code from Google
 * @param {string} state - State parameter for verification
 * @returns {Promise<Object>} Authentication result
 */
export const handleGoogleCallback = async (code, state) => {
  try {
    // Verify state parameter
    const storedState = sessionStorage.getItem('googleAuthState');
    if (state !== storedState) {
      throw new Error('Invalid state parameter. Possible CSRF attack.');
    }

    // Get the auth mode (login or register)
    const mode = sessionStorage.getItem('googleAuthMode') || 'login';
    
    // Send the authorization code to our backend
    const response = await apiFetch('/api/auth/google/callback/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        redirect_uri: REDIRECT_URI,
        mode,
        state,
        // Clickwrap evidence: continuing with sign-in/up implies acceptance of Terms/Privacy.
        accepted_legal: true,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Authentication failed (${response.status})`);
    }

    // Clear stored auth data
    sessionStorage.removeItem('googleAuthState');
    sessionStorage.removeItem('googleAuthMode');

    return {
      success: true,
      user: data.user,
      token: data.token,
      isNewUser: data.is_new_user || false
    };

  } catch (error) {
    // Clear stored auth data on error
    sessionStorage.removeItem('googleAuthState');
    sessionStorage.removeItem('googleAuthMode');
    
    console.error('Google auth callback error:', error);
    throw error;
  }
};

/**
 * Exchanges user info from Google for our app's authentication
 * @param {Object} userInfo - User information from Google
 * @param {string} mode - 'login' or 'register'
 * @returns {Promise<Object>} Authentication result
 */
export const authenticateWithUserInfo = async (userInfo, mode) => {
  try {
    const response = await apiFetch(`/api/auth/google/${mode}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        google_id: userInfo.id,
        email: userInfo.email,
        first_name: userInfo.given_name,
        last_name: userInfo.family_name,
        picture: userInfo.picture,
        verified_email: userInfo.verified_email
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Authentication failed (${response.status})`);
    }

    return {
      success: true,
      user: data.user,
      token: data.token,
      isNewUser: data.is_new_user || false
    };

  } catch (error) {
    console.error('Google authentication error:', error);
    throw error;
  }
};

/**
 * Validates Google OAuth configuration
 * @returns {boolean} True if configuration is valid
 */
export const validateGoogleConfig = () => {
  if (!GOOGLE_CLIENT_ID) {
    console.error('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID environment variable.');
    return false;
  }
  return true;
};

/**
 * Gets the current authentication mode from session storage
 * @returns {string} 'login', 'register', or 'login' as default
 */
export const getAuthMode = () => {
  return sessionStorage.getItem('googleAuthMode') || 'login';
};

/**
 * Clears all Google auth related session data
 */
export const clearGoogleAuthSession = () => {
  sessionStorage.removeItem('googleAuthState');
  sessionStorage.removeItem('googleAuthMode');
};

